/**
 * L'écran d'import d'un déboursé.
 *
 * Le geste du PRD §4.9 étape 1 : l'assistante reçoit un export DEPO de l'ERP, le
 * dépose, saisit le n° d'affaire, et l'application crée le chantier, archive le
 * devis figé et ouvre la copie de travail du conducteur.
 *
 * Ce que l'écran doit surtout faire, c'est **rendre compte**. Un import qui répond
 * « c'est fait » n'apprend rien : la question qui suit est toujours la même —
 * est-ce que les totaux sont ceux du classeur ? D'où le vis-à-vis recalculé /
 * annoncé / écart affiché en grand, et le lien direct vers la contre-étude, pour
 * comparer à l'écran avec le classeur ouvert à côté.
 */
import { useState } from "react";
import type { JSX } from "react";
import type { RapportImportVue } from "@planif/moteur";
import { chargerProchainCodeEtude, importerDepo } from "./api";

type Etat =
  | { phase: "saisie" }
  | { phase: "envoi" }
  | { phase: "importe"; rapport: RapportImportVue }
  | { phase: "erreur"; raison: string };

/** Les octets d'un fichier, en unité lisible. */
function taille(octets: number): string {
  return octets < 1024 * 1024
    ? `${Math.round(octets / 1024)} Ko`
    : `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * La vente retenue compose-t-elle exactement le montant du marché ?
 *
 * L'écart arrive **déjà formaté** par le serveur, à l'échelle du centime : le
 * comparer à zéro se fait donc sur la chaîne rendue, et non en la reconvertissant
 * en nombre — une conversion de plus dans un produit qui tient à ne jamais
 * approcher ses montants. Le signe est traité parce que `-0,00` s'écrit ainsi.
 */
function venteJuste(rapport: RapportImportVue): boolean {
  if (rapport.ecartVente === null) return false;
  return rapport.ecartVente.replace(/[-\s]/g, "") === "0,00";
}

/**
 * Le compte rendu d'un import réussi.
 *
 * L'ordre des blocs suit l'ordre des questions : est-ce que ça boucle, qu'est-ce
 * qui est entré, qu'est-ce qui demande un œil. Les listes de détail ne
 * s'affichent que si elles ont un contenu — un titre suivi de « aucun » occupe la
 * place de ce qui compte.
 */
function CompteRendu({
  rapport,
  onOuvrir,
  onRecommencer,
}: {
  rapport: RapportImportVue;
  onOuvrir: (chantierId: string, versionId: string) => void;
  onRecommencer: () => void;
}): JSX.Element {
  return (
    <section className="import__rapport">
      <h2>
        {rapport.apo} — {rapport.nomChantier}
      </h2>

      <p className="etat">
        Déboursé <code>{rapport.numeroDepo}</code>
        {rapport.rang === 0 ? " (déboursé initial)" : ` (complémentaire n° ${rapport.rang})`}, lu
        de <code>{rapport.fichierNom}</code>. Chantier{" "}
        {rapport.chantierCree ? "créé" : "déjà existant, ce déboursé s'y ajoute"}. Le devis est
        figé, la copie de travail du conducteur est ouverte.
      </p>

      {/* Les DEUX contrôles, côte à côte et pas l'un sans l'autre.
          Ils sont indépendants — le déboursé vient des temps unitaires et des prix
          de revient, la vente de la colonne des prix de vente — et c'est ce qui
          rend leur double égalité concluante : les mêmes postes écartés font
          tomber juste les charges ET le chiffre d'affaires. */}
      <table className="import__totaux">
        <tbody>
          <tr>
            <th>Déboursé recalculé</th>
            <td className="col-nombre">{rapport.deboursRecalcule} €</td>
          </tr>
          <tr>
            <th>Annoncé par le fichier</th>
            <td className="col-nombre">{rapport.deboursAnnonce} €</td>
          </tr>
          <tr className={rapport.conforme ? "conforme" : "ecart"}>
            <th>Écart</th>
            <td className="col-nombre">{rapport.ecartTotal} €</td>
          </tr>
          <tr className="import__totaux__separation">
            <th>Prix de vente retenu</th>
            <td className="col-nombre">{rapport.prixVenteRetenu} €</td>
          </tr>
          {rapport.montantMarcheHT !== null && (
            <tr>
              <th>Montant du marché HT</th>
              <td className="col-nombre">{rapport.montantMarcheHT} €</td>
            </tr>
          )}
          {rapport.ecartVente !== null && (
            <tr className={venteJuste(rapport) ? "conforme" : "ecart"}>
              <th>Écart</th>
              <td className="col-nombre">{rapport.ecartVente} €</td>
            </tr>
          )}
        </tbody>
      </table>

      <p className={rapport.conforme ? "conforme" : "alerte"}>
        {rapport.conforme
          ? "Le déboursé recalculé retombe sur celui du fichier."
          : `${rapport.anomaliesInexpliquees} écart(s) inexpliqué(s) sur ${rapport.anomalies} — ` +
            "le détail est plus bas."}
      </p>

      {rapport.ecartVente !== null && (
        <p className={venteJuste(rapport) ? "conforme" : "etat"}>
          {venteJuste(rapport)
            ? "Ce qui est retenu compose exactement le montant du marché : le périmètre est le bon."
            : `Il reste ${rapport.ecartVente} € entre la vente retenue et le montant du marché. ` +
              "C'est la remise négociée si le marché a été vendu moins cher que le chiffrage — " +
              "sinon, des options sont encore comptées et sont à décocher dans la contre-étude."}
        </p>
      )}

      <p className="etat">
        {rapport.chapitres} chapitres, {rapport.ouvrages} ouvrages, {rapport.ressources} lignes de
        ressources.
        {rapport.lignesIgnorees > 0 && (
          <>
            {" "}
            {rapport.lignesIgnorees} ligne(s) du classeur n'ont pas été rattachées et ne comptent
            pas.
          </>
        )}
      </p>

      {/* Des heures qu'aucun référentiel ne qualifie.
          Le pire cas est celui d'un référentiel VIDE : l'import réussit, les totaux
          du déboursé sont justes au centime, et seul le planning reste vide — sans
          rien qui le signale. Trouvé en éprouvant l'import sur une base où le
          référentiel n'était pas chargé. */}
      {rapport.lignesSansNature > 0 && (
        <div className="import__bloc">
          <h3>
            {rapport.heuresSansNature} h sur {rapport.lignesSansNature} ligne(s) sans nature
            connue
          </h3>
          <p className="alerte">
            Ces heures ne sont ni de la main-d'œuvre ni du matériel pour l'application : leurs
            codes sont absents du référentiel des ressources génériques. Elles compteront dans le
            déboursé, mais <strong>pas dans la durée des tâches</strong> — le planning les
            ignorera.
          </p>
          <p className="etat">
            À corriger dans « Ressources génériques » en ajoutant les codes manquants, puis en
            réimportant le déboursé. Si le référentiel est vide, le charger d'abord :{" "}
            <code>pnpm importer-generiques</code>
          </p>
        </div>
      )}

      {rapport.postesEcartes.length > 0 && (
        <div className="import__bloc">
          <h3>
            {rapport.postesEcartes.length} poste(s) sortis du chiffrage — options et variantes
          </h3>
          <p className="etat">
            Le fichier ne les compte pas : ils sont décochés d'entrée, et ne pèsent ni dans les
            charges, ni dans les heures, ni dans le chiffre d'affaires. Un clic dans la
            contre-étude les reprend.
          </p>
          <ul className="import__liste">
            {rapport.postesEcartes.map((poste) => (
              <li key={poste}>
                <code>{poste}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {rapport.anomaliesDetail.length > 0 && (
        <div className="import__bloc">
          <h3>{rapport.anomaliesDetail.length} écart(s) inexpliqué(s)</h3>
          <p className="etat">
            À regarder dans le classeur, aux lignes indiquées : le total du poste ne vaut pas la
            somme de son contenu, et rien dans le fichier ne l'explique.
          </p>
          <ul className="import__liste">
            {rapport.anomaliesDetail.map((texte, i) => (
              <li key={i} className="alerte">
                {texte}
              </li>
            ))}
          </ul>
        </div>
      )}

      {rapport.signalementsDetail.length > 0 && (
        <div className="import__bloc">
          <h3>{rapport.signalementsDetail.length} signalement(s)</h3>
          <p className="etat">
            Le déboursé boucle quand même : ce ne sont pas des écarts. Ce sont les cas qu'aucun
            total ne trahit — un ouvrage chiffré dont la quantité de vente est à zéro pèse 0 € et
            se voit nulle part. Oubli, ou poste en option ?
          </p>
          <ul className="import__liste">
            {rapport.signalementsDetail.map((texte, i) => (
              <li key={i}>{texte}</li>
            ))}
          </ul>
        </div>
      )}

      {rapport.troncatures.length > 0 && (
        <details className="import__bloc">
          <summary>
            {rapport.troncatures.length} valeur(s) annoncée(s) ramenée(s) à l'échelle du centime
          </summary>
          <p className="etat">
            Aucune valeur de calcul n'est concernée — le déboursé recalculé est intact. Le tableur
            calcule en virgule flottante et annonce parfois seize décimales là où le montant vaut
            deux.
          </p>
          <table className="import__troncatures">
            <thead>
              <tr>
                <th>Ligne</th>
                <th>Poste</th>
                <th>Colonne</th>
                <th>Fichier</th>
                <th>Écrit</th>
              </tr>
            </thead>
            <tbody>
              {rapport.troncatures.map((t, i) => (
                <tr key={i}>
                  <td className="col-nombre">{t.ligneTableur}</td>
                  <td className="code">{t.poste}</td>
                  <td>{t.colonne}</td>
                  <td className="col-nombre">{t.valeurFichier}</td>
                  <td className="col-nombre">{t.valeurEcrite}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <div className="import__actions">
        <button
          type="button"
          className="principal"
          onClick={() => {
            onOuvrir(rapport.chantierId, rapport.versionTravailId);
          }}
        >
          Ouvrir la contre-étude
        </button>
        <button type="button" onClick={onRecommencer}>
          Importer un autre déboursé
        </button>
      </div>
    </section>
  );
}

export function Import({
  onOuvrir,
  onImporte,
}: {
  /** Basculer sur la contre-étude qu'on vient d'importer. */
  onOuvrir: (chantierId: string, versionId: string) => void;
  /** Prévenir l'écran principal qu'un chantier est entré : sa liste a changé. */
  onImporte: () => void;
}): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "saisie" });
  const [fichier, setFichier] = useState<File | null>(null);
  const [apo, setApo] = useState("");
  const [nom, setNom] = useState("");
  const [client, setClient] = useState("");
  const [rang, setRang] = useState("0");

  const complet = fichier !== null && apo.trim() !== "" && nom.trim() !== "";
  const envoiEnCours = etat.phase === "envoi";

  const envoyer = (): void => {
    if (fichier === null || !complet) return;
    setEtat({ phase: "envoi" });

    importerDepo({ fichier, apo: apo.trim(), nom: nom.trim(), client: client.trim(), rang })
      .then(({ rapport }) => {
        setEtat({ phase: "importe", rapport });
        // La liste des chantiers de l'écran principal est périmée dès cet
        // instant : sans ce signal, le chantier importé n'apparaîtrait qu'au
        // prochain rechargement de la page.
        onImporte();
      })
      .catch((erreur: unknown) => {
        setEtat({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });
  };

  const recommencer = (): void => {
    setEtat({ phase: "saisie" });
    setFichier(null);
    setApo("");
    setNom("");
    setClient("");
    setRang("0");
  };

  if (etat.phase === "importe") {
    return (
      <CompteRendu rapport={etat.rapport} onOuvrir={onOuvrir} onRecommencer={recommencer} />
    );
  }

  return (
    <>
      <p className="etat">
        Déposer l'export DEPO de l'ERP. Le fichier n'est pas modifié et n'est pas conservé : il est
        lu, puis oublié. L'application en tire le devis figé — intouchable, il fait référence — et
        la copie de travail sur laquelle le conducteur fait sa contre-étude.
      </p>

      {etat.phase === "erreur" && (
        <p className="alerte" role="alert">
          {etat.raison}
        </p>
      )}

      <form
        className="import__formulaire"
        onSubmit={(e) => {
          e.preventDefault();
          envoyer();
        }}
      >
        <label className="import__fichier">
          Classeur du déboursé
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={envoiEnCours}
            onChange={(e) => {
              setFichier(e.target.files?.[0] ?? null);
              // Une nouvelle tentative après un refus : l'ancien message ne
              // décrit plus ce qui est à l'écran.
              if (etat.phase === "erreur") setEtat({ phase: "saisie" });
            }}
          />
          {fichier !== null && (
            <span className="etat">
              {fichier.name} — {taille(fichier.size)}
            </span>
          )}
        </label>

        <label>
          N° d'affaire (APO)
          <input
            value={apo}
            placeholder="APO260098"
            disabled={envoiEnCours}
            onChange={(e) => {
              setApo(e.target.value);
            }}
          />
          <span className="etat">
            Il n'est pas inscrit dans le déboursé : c'est lui qui rattache le fichier au bon
            chantier.
          </span>
          {/* Une étude du bureau d'études (095) : pas encore d'APO, l'affaire n'est pas signée.
              Le code proposé est le suivant de la série ; seul le BE verra l'étude, jusqu'à ce
              qu'on lui rattache son APO. */}
          <button
            type="button"
            className="lien"
            disabled={envoiEnCours}
            onClick={() => {
              chargerProchainCodeEtude()
                .then(({ code }) => {
                  setApo(code);
                })
                .catch((erreur: unknown) => {
                  setEtat({
                    phase: "erreur",
                    raison: erreur instanceof Error ? erreur.message : String(erreur),
                  });
                });
            }}
          >
            Étude du bureau d&apos;études, sans APO : prendre le code suivant
          </button>
          {apo.trim().toUpperCase().startsWith("ETUDE") && (
            <span className="etat">
              Étude : seuls le bureau d&apos;études et les administrateurs la verront. Quand l&apos;affaire sera signée, on lui
              rattachera son APO depuis la liste des chantiers, et elle passera à la référente
              administrative.
            </span>
          )}
        </label>

        <label>
          Nom du chantier
          <input
            value={nom}
            placeholder="Rue des Écoles"
            disabled={envoiEnCours}
            onChange={(e) => {
              setNom(e.target.value);
            }}
          />
        </label>

        <label>
          Client <span className="facultatif">(facultatif)</span>
          <input
            value={client}
            disabled={envoiEnCours}
            onChange={(e) => {
              setClient(e.target.value);
            }}
          />
        </label>

        <label>
          Rang du déboursé
          <input
            value={rang}
            disabled={envoiEnCours}
            onChange={(e) => {
              setRang(e.target.value);
            }}
          />
          <span className="etat">
            0 pour le déboursé initial, 1 et suivants pour les complémentaires du même chantier.
          </span>
        </label>

        <div className="import__actions">
          <button type="submit" className="principal" disabled={!complet || envoiEnCours}>
            {envoiEnCours ? "Import en cours…" : "Importer"}
          </button>
        </div>
      </form>

      {envoiEnCours && (
        <p className="etat">
          Lecture du classeur, écriture en base et relecture pour contrôle. Rien n'est conservé si
          le déboursé relu ne redonne pas exactement le fichier.
        </p>
      )}
    </>
  );
}
